import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpService } from '../../../utils/apiService';

export const useFetchEvVehicleModels = ({ page = 1, search = '', brand = '', limit = 20 } = {}, options = {}) => {
  return useQuery({
    queryKey: ['ev-vehicle-models', page, search, brand, limit],
    queryFn: async () => {
      const response = await httpService.post('ev-vehicle-model/list', {
        page,
        limit,
        search: search || undefined,
        brand: brand || undefined,
      });
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useFetchEvVehicleBrands = (options = {}) => {
  return useQuery({
    queryKey: ['ev-vehicle-brands'],
    queryFn: async () => {
      const response = await httpService.post('ev-vehicle-model/brands', {});
      return response.data.data?.brands || [];
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useCreateEvVehicleModel = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation({
    mutationKey: ['create-ev-vehicle-model'],
    mutationFn: async (data) => {
      const response = await httpService.post('ev-vehicle-model/create', data);
      return response.data.data;
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['ev-vehicle-models'] });
      queryClient.invalidateQueries({ queryKey: ['ev-vehicle-brands'] });
      onSuccess?.(...args);
    },
    ...rest,
  });
};

export const useUpdateEvVehicleModel = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation({
    mutationKey: ['update-ev-vehicle-model'],
    mutationFn: async ({ id, ...data }) => {
      const response = await httpService.post(`ev-vehicle-model/update/${id}`, data);
      return response.data.data;
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['ev-vehicle-models'] });
      queryClient.invalidateQueries({ queryKey: ['ev-vehicle-brands'] });
      onSuccess?.(...args);
    },
    ...rest,
  });
};

export const useDeleteEvVehicleModel = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation({
    mutationKey: ['delete-ev-vehicle-model'],
    mutationFn: async (id) => {
      const response = await httpService.post(`ev-vehicle-model/delete/${id}`);
      return response.data.data;
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['ev-vehicle-models'] });
      queryClient.invalidateQueries({ queryKey: ['ev-vehicle-brands'] });
      onSuccess?.(...args);
    },
    ...rest,
  });
};

export const useFetchCustomerVehicles = (customerId, options = {}) => {
  return useQuery({
    queryKey: ['customer-vehicles', customerId],
    queryFn: async () => {
      const response = await httpService.post('vehicle/list', {
        customerId,
        page: 1,
        limit: 50,
      });
      return response.data.data;
    },
    enabled: !!customerId,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useCreateCustomerVehicle = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation({
    mutationKey: ['create-customer-vehicle'],
    mutationFn: async (data) => {
      const response = await httpService.post('vehicle/create', data);
      return response.data.data;
    },
    onSuccess: (data, variables, ...restArgs) => {
      if (variables?.customerId) {
        queryClient.invalidateQueries({
          queryKey: ['customer-vehicles', variables.customerId],
        });
      }
      onSuccess?.(data, variables, ...restArgs);
    },
    ...rest,
  });
};
